import { useEffect } from "react";

/*
  Drag a table sideways by grabbing it.

  Asset Management grew this first and keeps its own copy, because there it has
  to arbitrate with the row reorder -- a press can become either, and the whole
  of that file's panGesture.js exists to decide which. Everywhere else there is
  nothing to arbitrate with, so this is that behaviour with the arbitration
  taken out: the rules that are NOT about reordering, in one place.

  What is kept, and why each one is load-bearing:

  - Three pixels before a press counts as a drag, so an ordinary click on a row
    is not stolen by a hand that moved while clicking.
  - Sideways only. A press that sets off vertically is the page being scrolled
    and is left alone.
  - Presses on a control, a field or the scrollbar are not ours.
  - Selection is off for the life of the press. It has to be off from the FIRST
    move: a selection already under way is not cancelled by turning user-select
    off later, which is what made a drag highlight its way across the table.
  - The click that ends a drag is swallowed, so letting go does not also press
    whatever the pointer finished over.
  - The listeners live on the document, so the drag survives the pointer
    leaving the box, and a button released outside the window is noticed on the
    next move rather than leaving the drag stuck on.
*/

const MIN_TRAVEL = 3;
// Far enough that the reader saw the table move, rather than a wobble.
const CLICK_TRAVEL = 4;

const IGNORED = "input, textarea, select, button, a, [role='option'], [data-dropdown-menu]";

export const attachDragScroll = (el) => {
  if (!el) return () => {};

  let start = null;
  let pan = null;
  let suppressClick = false;
  let teardown = null;

  /*
    Mouse only, and that is not a detail.

    A touch already scrolls this box natively. Taking the press here would
    fight that, and worse: the drag runs on document MOUSE events, which a
    finger never sends -- so `pan` would be set with no mouseup coming to
    clear it, the box would refuse every press afterwards, and the swallowed
    click would eat the next tap.
  */
  const onDown = (event) => {
    if (event.pointerType !== "mouse" || event.button !== 0 || pan) return;
    if (event.target instanceof Element && event.target.closest(IGNORED)) return;
    /*
      A press on the box's own scrollbar belongs to the browser. The bar has no
      element of its own, so it arrives with `target` set to the box and walks
      past the test above; clientHeight stops short of it while the bounding
      rect does not, so anything past that line is the bar. Without this the
      drag and the scrollbar both drive scrollLeft from one mouse, pixel for
      pixel against thumb-distance, and the content is thrown between the two.
    */
    const rect = el.getBoundingClientRect();
    if (event.clientY - rect.top > el.clientHeight) return;
    if (event.clientX - rect.left > el.clientWidth) return;

    start = { x: event.clientX, y: event.clientY };
    /*
      Selection is NOT switched off here, and that is the difference between
      this and the Asset Management table.

      There, a press on a row is a drag of the row, so nothing is selectable
      anyway and killing selection on the way down costs nothing. Here the
      rows are data people copy -- an address, a phone number -- and turning
      selection off at pointerdown made the whole table unhighlightable. It
      is switched off only once this has actually claimed the gesture, below.
    */
  };

  const onDecide = (event) => {
    if (event.pointerType !== "mouse" || !start || pan) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < MIN_TRAVEL && Math.abs(dy) < MIN_TRAVEL) return;
    // Downwards: the page is being scrolled, not this box.
    if (Math.abs(dx) <= Math.abs(dy)) {
      release();
      return;
    }
    // Nothing to scroll to: leave the press alone.
    if (el.scrollWidth <= el.clientWidth) {
      release();
      return;
    }
    pan = { x: start.x, scrollLeft: el.scrollLeft };
    /*
      Now, because the press has turned out to be a sideways drag.

      The three pixels before this point may have begun a selection, and a
      browser does not abandon one just because user-select changed -- so the
      stray highlight is cleared as well, or the table ends every drag with a
      word or two picked out.
    */
    el.style.userSelect = "none";
    const selection = window.getSelection && window.getSelection();
    if (selection && !selection.isCollapsed) selection.removeAllRanges();

    const onMove = (move) => {
      if (!pan) return;
      // Released while outside the window: the mouseup never reached us.
      if (move.buttons === 0) {
        up();
        return;
      }
      move.preventDefault();
      el.scrollLeft = pan.scrollLeft - (move.clientX - pan.x);
      if (Math.abs(move.clientX - pan.x) > CLICK_TRAVEL) suppressClick = true;
    };
    function up() {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", up);
      teardown = null;
      pan = null;
      release();
    }
    teardown = up;
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", up);
  };

  function release() {
    start = null;
    el.style.userSelect = "";
  }

  const onUp = () => {
    if (!pan) release();
  };

  const onClick = (event) => {
    if (!suppressClick) return;
    suppressClick = false;
    event.stopPropagation();
    event.preventDefault();
  };

  el.addEventListener("pointerdown", onDown);
  el.addEventListener("pointermove", onDecide);
  el.addEventListener("pointerup", onUp);
  el.addEventListener("click", onClick, true);

  return () => {
    if (teardown) teardown();
    el.removeEventListener("pointerdown", onDown);
    el.removeEventListener("pointermove", onDecide);
    el.removeEventListener("pointerup", onUp);
    el.removeEventListener("click", onClick, true);
    release();
  };
};

/*
  The same thing for a ref. Re-attaches when the ref's element changes, and
  tears the listeners down on unmount -- including a drag still in progress,
  which would otherwise keep its document listeners after the box has gone.
*/
export const useDragScroll = (ref) => {
  useEffect(() => attachDragScroll(ref && ref.current), [ref]);
};
